<template>
  <PaymentIssueBanner
    v-if="applicable"
    class="mb-[3px]"
    :org-id="orgId"
    :subscription="subscription"
    square
  />

  <Toast
    v-model:model-value="showToast"
    :message="toastMessage"
    position="bottom-right"
    :duration="4000"
  />
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { Org } from "tf-api-client";
import { useAuth } from "../../composables/use-auth";
import { useBillingReturn } from "../../composables/use-billing-portal";
import { useOrg } from "../../composables/use-org";
import { useOrgSubscription } from "../../composables/use-org-subscription";
import {
  BILLING_RETURN_REFETCH_DELAY_MS,
  SUBSCRIPTION_ROUTE,
} from "../../config/licensing";
import Toast from "../Base/Toast.vue";
import PaymentIssueBanner from "./PaymentIssueBanner.vue";

// The payment banner on every page of the app shell, for the selected
// organization. Mounted only for its org admins (see layouts/dashboard.vue).

const { t } = useI18n();
const route = useRoute();
const { selectedMembership } = useAuth();
const { org } = useOrg();
// shares its cache with the subscription page
const { subscription, fetchSubscription } = useOrgSubscription();

const orgId = computed(() => selectedMembership.value?.organizationId ?? null);

const applicable = computed(
  () =>
    // the org of the current selection, loaded: a Managed org (whose
    // subscription is its parent's) must not flash the banner meanwhile
    !!org.value &&
    org.value.id === orgId.value &&
    org.value.kind !== Org.OrganizationKind.Managed &&
    // the subscription page shows it itself
    route.path !== SUBSCRIPTION_ROUTE,
);

const showToast = ref(false);
const toastMessage = ref("");

// Back from the portal opened by a shell banner (this one or the trial
// banner, mounted alongside): the page was just loaded, so fetch the
// subscription once more after the provider's webhooks had time to arrive.
// The subscription page handles its own return.
let returnRefetch: ReturnType<typeof setTimeout> | undefined;
useBillingReturn(
  () => {
    toastMessage.value = t("licensing.billing.returned");
    showToast.value = true;
    returnRefetch = setTimeout(fetchSubscription, BILLING_RETURN_REFETCH_DELAY_MS);
  },
  () => route.path !== SUBSCRIPTION_ROUTE,
);
onUnmounted(() => clearTimeout(returnRefetch));
</script>
